import { motion } from "framer-motion";
import { Users, Target, Clock, HeartHandshake } from "lucide-react";
import { ASSETS } from "@/data";

const features = [
  { icon: Users, title: "Ολιγομελή τμήματα", desc: "Τμήματα μέχρι 5 μαθητές & group των 2 για ουσιαστική, προσωπική επαφή." },
  { icon: Target, title: "Δικό μας εκπαιδευτικό υλικό", desc: "Βιβλία & τράπεζες θεμάτων γραμμένα από τους καθηγητές μας." },
  { icon: Clock, title: "Συνεχής υποστήριξη", desc: "Ώρες μελέτης, απορίες & ατομικό πλάνο για κάθε μαθητή." },
  { icon: HeartHandshake, title: "Ανθρώπινη σχέση", desc: "Καθηγητές με εμπειρία και όραμα που εμπνέουν και στηρίζουν." },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="py-20 md:py-32 bg-secondary/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-16">
          <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-4">// Η Φιλοσοφία μας</div>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
            Δεν μαθαίνουμε απλώς την ύλη. Δημιουργούμε τρόπο σκέψης.
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Το όνομά μας δεν είναι τυχαίο. Η <strong className="text-foreground">Φυσική Επιλογή</strong> πιστεύει
            ότι κάθε μαθητής μπορεί να διακριθεί όταν βρεθεί στο σωστό περιβάλλον. Επιλέγουμε την ποιότητα
            έναντι της ποσότητας — σε κάθε τμήμα, σε κάθε μάθημα.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fade}
            className="md:col-span-8 lg:col-span-4 lg:row-span-2 relative rounded-sm overflow-hidden min-h-[300px]"
          >
            <img
              src={ASSETS.books}
              alt="Βιβλία & τράπεζες θεμάτων της Φυσικής Επιλογής"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-primary/60 mix-blend-multiply" />
            <div className="relative p-8 lg:p-12 h-full flex flex-col justify-end text-white">
              <div className="font-heading text-2xl lg:text-3xl font-extrabold leading-tight">Δικό μας εκπαιδευτικό υλικό</div>
              <p className="font-mono-accent text-xs uppercase tracking-widest mt-2 text-white/80">
                Βιβλία & τράπεζες θεμάτων από τους καθηγητές μας
              </p>
            </div>
          </motion.div>

          {features.map((f, i) => (
            <motion.div
              key={f.title}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fade}
              data-testid={`about-feature-${i}`}
              className="md:col-span-4 lg:col-span-4 bg-white border border-border rounded-sm p-8 hover:border-primary transition-colors"
            >
              <f.icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
              <h3 className="font-heading font-bold text-lg mt-5">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
